import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { fetchApi } from "../../auth/api_two";
import Nav from "../../components/Home/Nav";
import Footer from "../../components/Home/Footer";
import { getImageUrl } from "../../utils/imageHelper";
import {
  HiSearch,
  HiCollection,
  HiBriefcase,
  HiBookOpen,
  HiPhotograph,
  HiX,
  HiArrowRight,
  HiFilter,
} from "react-icons/hi";

const CATEGORIES = [
  { key: "all", label: "All Results", icon: <HiSearch /> },
  { key: "serviceCategories", label: "Service Categories", icon: <HiCollection /> },
  { key: "serviceList", label: "Service List", icon: <HiBriefcase /> },
  { key: "guideTypes", label: "Guide Types", icon: <HiBookOpen /> },
  { key: "guideList", label: "Guide List", icon: <HiBookOpen /> },
  { key: "portfolio", label: "Portfolio", icon: <HiPhotograph /> },
];

function ResultCard({ item, type }) {
  const imgSrc = getImageUrl(item.image || item.service_image || item.image_url || null);

  const linkMap = {
    serviceCategories: `/childServices/${item.id}`,
    serviceList: `/childServices/${item.parent_id || item.id}`,
    guideTypes: `/guideType/${item.guide_id || item.id}`,
    guideList: `/guideType/${item.id}`,
    portfolio: `/portfolio`,
  };

  const tagColors = {
    serviceCategories: "bg-blue-100 text-blue-700",
    serviceList: "bg-indigo-100 text-indigo-700",
    guideTypes: "bg-emerald-100 text-emerald-700",
    guideList: "bg-teal-100 text-teal-700",
    portfolio: "bg-violet-100 text-violet-700",
  };

  const tagLabels = {
    serviceCategories: "Category",
    serviceList: "Service",
    guideTypes: "Guide Type",
    guideList: "Guide",
    portfolio: "Portfolio",
  };

  return (
    <Link
      to={linkMap[type] || "#"}
      className="group flex items-center gap-4 bg-white hover:bg-slate-50 rounded-2xl border border-slate-100 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-50 transition-all duration-300 p-4 active:scale-[0.99]"
    >
      {/* Image */}
      <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-100 shrink-0">
        {imgSrc ? (
          <img src={imgSrc} alt={item.name || item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300 text-2xl">
            {tagLabels[type]?.[0]}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${tagColors[type]}`}>
            {tagLabels[type]}
          </span>
        </div>
        <h3 className="font-black text-slate-900 text-sm capitalize truncate group-hover:text-blue-600 transition-colors">
          {item.name || item.title || "Untitled"}
        </h3>
        {(item.description) && (
          <p className="text-[11px] text-slate-400 font-medium line-clamp-1 mt-0.5">
            {item.description}
          </p>
        )}
      </div>

      {/* Arrow */}
      <HiArrowRight className="text-slate-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all shrink-0" size={18} />
    </Link>
  );
}

function SearchPage() {
  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [data, setData] = useState({
    serviceCategories: [],
    serviceList: [],
    guideTypes: [],
    guideList: [],
    portfolio: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [catRes, servRes, guideTypesRes, guideRes, portRes] = await Promise.allSettled([
          fetchApi("get", "servicesWithNullParent"),
          fetchApi("get", "servicesWithParent"),
          fetchApi("get", "guideTypes"),
          fetchApi("get", "guides"),
          fetchApi("get", "portfolios"),
        ]);

        setData({
          serviceCategories: catRes.status === "fulfilled" ? catRes.value.data || [] : [],
          serviceList: servRes.status === "fulfilled" ? servRes.value.data || [] : [],
          guideTypes: guideTypesRes.status === "fulfilled" ? guideTypesRes.value.data || [] : [],
          guideList: guideRes.status === "fulfilled" ? guideRes.value.data || [] : [],
          portfolio: portRes.status === "fulfilled" ? portRes.value.data || [] : [],
        });
      } catch (e) {
        console.error("Search fetch error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const filterItems = useCallback(
    (items) => {
      if (!query.trim()) return items;
      const q = query.toLowerCase();
      return items.filter(
        (item) =>
          (item.name || "").toLowerCase().includes(q) ||
          (item.title || "").toLowerCase().includes(q) ||
          (item.description || "").toLowerCase().includes(q)
      );
    },
    [query]
  );

  const results = {
    serviceCategories: filterItems(data.serviceCategories),
    serviceList: filterItems(data.serviceList),
    guideTypes: filterItems(data.guideTypes),
    guideList: filterItems(data.guideList),
    portfolio: filterItems(data.portfolio),
  };

  const totalCount = Object.values(results).reduce((acc, arr) => acc + arr.length, 0);

  const visibleSections =
    activeFilter === "all"
      ? CATEGORIES.slice(1)
      : CATEGORIES.filter((c) => c.key === activeFilter);

  return (
    <div className="bg-slate-50 min-h-screen">
      <Nav />

      {/* Hero Search Bar */}
      <section className="pt-28 pb-0 bg-white border-b border-slate-100 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 md:px-6 pb-6">
          <div className="flex flex-col items-center text-center mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-50 text-blue-600 rounded-full text-[10px] font-black uppercase tracking-widest mb-3">
              <HiSearch size={12} /> Global Search
            </span>
            <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">
              Find Anything Instantly
            </h1>
            <p className="text-slate-400 text-sm font-medium mt-2">
              Search across all service categories, services, guides, and portfolio
            </p>
          </div>

          {/* Search Input */}
          <div className="relative">
            <HiSearch className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={22} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. Waterproofing, Membrane, Grouting..."
              autoFocus
              className="w-full pl-14 pr-14 py-4 md:py-5 text-slate-900 font-semibold text-sm md:text-base bg-slate-50 border-2 border-slate-200 focus:border-blue-500 focus:bg-white rounded-2xl outline-none transition-all duration-300 shadow-inner"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-5 top-1/2 -translate-y-1/2 p-1 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-600 transition-colors"
              >
                <HiX size={16} />
              </button>
            )}
          </div>

          {/* Filter Tabs */}
          <div className="flex items-center gap-2 mt-4 overflow-x-auto pb-1 scrollbar-hide">
            {CATEGORIES.map((cat) => {
              const count =
                cat.key === "all"
                  ? totalCount
                  : (results[cat.key] || []).length;
              return (
                <button
                  key={cat.key}
                  onClick={() => setActiveFilter(cat.key)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all duration-200 border shrink-0 ${
                    activeFilter === cat.key
                      ? "bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-200"
                      : "bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-600"
                  }`}
                >
                  {cat.icon} {cat.label}
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-black ${activeFilter === cat.key ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Results */}
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-10 pb-24 space-y-10">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            <p className="text-slate-400 font-bold text-xs uppercase tracking-widest">Loading data...</p>
          </div>
        ) : totalCount === 0 && query ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <HiSearch size={56} className="text-slate-200" />
            <p className="text-xl font-black text-slate-800">No results for "{query}"</p>
            <p className="text-slate-400 text-sm">Try a different keyword or browse all categories below.</p>
            <button
              onClick={() => setQuery("")}
              className="mt-3 px-6 py-2.5 bg-blue-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-900 transition-all"
            >
              Clear Search
            </button>
          </div>
        ) : (
          visibleSections.map((cat) => {
            const items = results[cat.key] || [];
            if (items.length === 0 && query) return null;
            return (
              <div key={cat.key}>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-blue-600 text-lg">{cat.icon}</span>
                  <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest">
                    {cat.label}
                  </h2>
                  <span className="ml-1 px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full text-[10px] font-black">
                    {items.length}
                  </span>
                </div>
                {items.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-slate-100 px-6 py-8 text-center">
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">
                      No {cat.label.toLowerCase()} yet
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {items.map((item) => (
                      <ResultCard key={item.id} item={item} type={cat.key} />
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </main>

      <Footer />
    </div>
  );
}

export default SearchPage;
